import * as React from 'react'
import { WrappedFieldProps } from 'redux-form'
import cn from 'classnames'
import * as sMarg from 'styles/modules/margins.scss'
import * as sLay from 'styles/modules/layout.scss'
import {
  useGetPhoneSystemExtensionsQuery,
  GetPhoneSystemExtensionsQuery,
} from 'src/generated/graphql'
import LoadingDots from 'components/Loading/LoadingDots'
import { Checkbox } from 'components/Input/Checkbox'
import sText from 'components/Text/Text.scss'
import { DragDropContext, Droppable, Draggable, DragDropContextProps } from 'react-beautiful-dnd'
import { keyBy } from 'lodash'

const reorder = <T extends any>(list: T[], startIndex: number, endIndex: number) => {
  const result = [...list]
  const element = result[startIndex]
  result.splice(startIndex, 1)
  result.splice(endIndex, 0, element)
  return result
}

const ExtensionItem = ({
  onChange,
  item,
  value,
  position,
}: {
  onChange: (key: string, value: boolean) => void
  value: boolean
  item: GetPhoneSystemExtensionsQuery['phoneSystemExtensions'][0]
  position?: number
}) => {
  return (
    <div
      key={item.id}
      className={cn(
        sLay['layout-row'],
        sLay['layout-align-start-center'],
        sText.descrip3,
        sMarg.mb15
      )}
    >
      <Checkbox
        input={{
          value,
          onChange: (newValue: boolean) => onChange(item.id, newValue),
        }}
        meta={{}}
        style={{ marginRight: '10px' }}
      />
      <div className={sLay.flex}>
        {item.customerReference} ({item.number})
      </div>
      {position && <div>Order: {position}</div>}
    </div>
  )
}

export interface IExtensionSelectionProps extends WrappedFieldProps {
  phoneSystemId: string
}

export const ExtensionsSelect = (props: IExtensionSelectionProps) => {
  const { phoneSystemId } = props
  const selectedIds = props.input.value

  const { data, loading } = useGetPhoneSystemExtensionsQuery({
    variables: {
      phoneSystemId,
    },
  })

  // Some callbacks
  const onChange = (key: string, value: boolean) => {
    const newVal =
      value === true ? [...props.input.value, key] : props.input.value.filter(item => item !== key)
    props.input.onChange(newVal)
  }

  const onDragEnd: DragDropContextProps['onDragEnd'] = result => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const items = reorder(selectedIds, result.source.index, result.destination.index)
    props.input.onChange(items)
  }

  // Loading indicator
  if (!data || loading) {
    return <LoadingDots />
  }

  // Get results
  const results = data?.phoneSystemExtensions || []
  const resultsById = keyBy(results, 'id')
  const resultsNotSelected = results.filter(result => !selectedIds.includes(result.id))

  return (
    <div>
      <div className={cn(sText.descrip3, sMarg.mb15)}>Extensions: </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {selectedIds.map((itemId, index) => {
                const item = resultsById[itemId]
                return (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <ExtensionItem
                          item={item}
                          value={true}
                          onChange={onChange}
                          position={index + 1}
                        />
                      </div>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>

        {resultsNotSelected.map(item => (
          <ExtensionItem key={item.id} item={item} value={false} onChange={onChange} />
        ))}
      </DragDropContext>
    </div>
  )
}



// WEBPACK FOOTER //
// src/features/PhoneSystem/PhoneSystemHuntGroupEditForm/ExtensionsSelect.tsx